/**
 * Party-related constants
 *
 * These enums provide runtime-accessible values for party decisions,
 * voting, and location tracking.
 */

/**
 * Party joint decision status enum
 * Use these instead of string literals like 'pending' or 'passed'
 */
export enum DecisionStatusEnum {
  PENDING = 'pending',
  PASSED = 'passed',
  FAILED = 'failed',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  /** Used in game context for rejected decisions */
  REJECTED = 'rejected',
}

/**
 * Party joint decision type enum
 * Use these instead of string literals like 'start-game' or 'kick-member'
 */
export enum DecisionTypeEnum {
  // Lobby decisions
  START_GAME = 'start-game',
  CONTINUE_GAME = 'continue-game',
  ACCEPT_MEMBER = 'accept-member',
  INVITE_CHAR = 'invite-char',
  KICK_MEMBER = 'kick-member',
  SELECT_MODULE = 'select-module',
  SET_MEMBERSHIP_STATE = 'set-membership-state',
  RENAME_PARTY = 'rename-party',
  // In-game decisions
  END_SESSION = 'end-session',
  SHORT_REST = 'short-rest',
  LONG_REST = 'long-rest',
  TRAVEL = 'travel',
}

/**
 * Vote value enum
 * Use these instead of string literals like 'yes' or 'no'
 */
export enum VoteEnum {
  YES = 'yes',
  NO = 'no',
}

/**
 * Party location enum
 * Use these instead of string literals like 'lobby' or 'game'
 */
export enum PartyLocationEnum {
  LOBBY = 'lobby',
  GAME = 'game',
}

/**
 * Party membership state enum
 * Use these instead of string literals like 'open' or 'closed'
 */
export enum MembershipStateEnum {
  OPEN = 'open',
  CLOSED = 'closed',
}

/**
 * How long a pending party invite stays live before the server sweeps it.
 *
 * Mirror of `PARTY_INVITE_TTL_DAYS` in
 * `backend/lobby-server/src/constants.rs`. The server is the source of
 * truth: it stamps `expiresAt` on the invite. The client only needs this
 * to fill in a local expiry when a push arrives without one.
 */
export const PARTY_INVITE_TTL_DAYS = 7;

/** {@link PARTY_INVITE_TTL_DAYS} in milliseconds. */
export const PARTY_INVITE_TTL_MS = PARTY_INVITE_TTL_DAYS * 24 * 60 * 60 * 1000;
